import React from 'react';
import { Link } from 'react-router-dom';
import { GridCellProps } from '@progress/kendo-react-grid';
import { buildRoute, fullClassName, toggledClass } from 'Utilities';
import { DateCellProps } from '../InputCells/DateCell';
import { GridInput } from '../InputCells/GridInput';
import { InputType } from 'Models';
import { dayjs } from 'Utilities/DateTimeUtc';

export type LinkCellProps = {
  endpoint?: string;
  linkField: string;
  viewLinkField?: string;
  subRoute?: string;
  openInNewTab?: boolean;
  forceReload?: boolean;
  wrappable?: boolean;
  convertFunction?: any;
  customClassName?: string;
  bold?: boolean;
  style?: React.CSSProperties;
  to?: string;
};

export type PureCellProps = {
  onClick?: (
    event: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>
  ) => void;
  linkField: string;
  viewLinkField?: string;
  subRoute?: string;
  openInNewTab?: boolean;
  forceReload?: boolean;
  wrappable?: boolean;
  convertFunction?: any;
  customClassName?: string;
  bold?: boolean;
  style?: React.CSSProperties;
};

export function getLinkCell(props: LinkCellProps) {
  return (gridCellProps: GridCellProps) => (
    <LinkCell {...gridCellProps} {...props} />
  );
}

export const PureCell: React.FC<GridCellProps & PureCellProps> = ({
  dataItem,
  field,
  style,
  bold,
  onClick,
}) => {
  return (
    <td style={style} onClick={onClick}>
      <span className={fullClassName(toggledClass('bold-table-cell', bold))}>
        {dataItem[field as string]}
      </span>
    </td>
  );
};

export const LinkCell: React.FC<GridCellProps & LinkCellProps> = ({
  dataItem,
  field,
  endpoint,
  subRoute,
  linkField,
  openInNewTab,
  viewLinkField,
  forceReload,
  wrappable,
  convertFunction,
  bold,
  style,
  className,
  to,
  children
}) => {
  const value = dataItem[field as string];
  const linkValue = dataItem[linkField];
  const viewLinkValue =
    viewLinkField === undefined ? '' : dataItem[viewLinkField];

  function refreshPage() {
    setTimeout(() => {
      window.location.reload();
    }, 10);
  }

  return (
    <td
      className={fullClassName(
        className ?? 'color-primary',
        toggledClass('whitespace-preline', wrappable)
      )}
      style={style}
    >
      {openInNewTab ? (
        <Link
          className={fullClassName(toggledClass('bold-table-cell', bold))}
          to={to ?? buildRoute(endpoint, linkValue, subRoute, viewLinkValue)}
          target="_blank"
        >
          {convertFunction ? convertFunction(value) : value}
        </Link>
      ) : forceReload ? (
        <Link
          className={fullClassName(toggledClass('bold-table-cell', bold))}
          to={to ?? buildRoute(endpoint, linkValue, subRoute, viewLinkValue)}
          onClick={refreshPage}
        >
          {convertFunction ? convertFunction(value) : value}
        </Link>
      ) : (
        <Link
          className={fullClassName(toggledClass('bold-table-cell', bold))}
          to={to ?? buildRoute(endpoint, linkValue, subRoute, viewLinkValue)}
        >
          {convertFunction ? convertFunction(value) : value}
        </Link>
      )}
      {children}
    </td>
  );
};

export const DateLinkCell: React.FC<
  GridCellProps & LinkCellProps & DateCellProps
> = ({
  dataItem,
  field,
  onChange,
  endpoint,
  subRoute,
  linkField,
  openInNewTab,
  viewLinkField,
  forceReload,
  wrappable,
  dateFormat,
  customClassName,
  disableUtcConversion,
}) => {
  const value = dataItem[field as string];
  const linkValue = dataItem[linkField];
  const viewLinkValue =
    viewLinkField === undefined ? '' : dataItem[viewLinkField];

  function refreshPage() {
    setTimeout(() => {
      window.location.reload();
    }, 10);
  }

  const getFormattedDate = () => {
    if (!value) return '';
    const d = disableUtcConversion ? dayjs(value) : dayjs.utc(value).local();
    if (!d.isValid() || d.year() <= 1) return '';
    return d.format(dateFormat || 'l');
  };

  const renderContent = () => {
    if (dataItem.inEdit) {
      return (
        <GridInput
          dataItem={dataItem}
          name={field as string}
          type={InputType.DatePicker}
          onChange={onChange}
          value={value}
        />
      );
    }
    return getFormattedDate();
  };

  return (
    <td
      className={fullClassName(
        'color-primary',
        toggledClass('whitespace-preline', wrappable),
        customClassName
      )}
    >
      {openInNewTab ? (
        <Link
          to={buildRoute(endpoint, linkValue, subRoute, viewLinkValue)}
          target="_blank"
        >
          {renderContent()}
        </Link>
      ) : forceReload ? (
        <Link
          to={buildRoute(endpoint, linkValue, subRoute, viewLinkValue)}
          onClick={refreshPage}
        >
          {renderContent()}
        </Link>
      ) : (
        <Link to={buildRoute(endpoint, linkValue, subRoute, viewLinkValue)}>
          {renderContent()}
        </Link>
      )}
    </td>
  );
};
